import { SharedResultLink, CreateShareLinkRequest } from "@/types/sharing/sharing.types";
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc, addDoc } from "firebase/firestore";
import { db } from "@/_db";

const sharingService = {

  // Generate a unique token for sharing
  generateUniqueToken(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  },

  // Create a new shared link
  generateShareLink: async (request: CreateShareLinkRequest): Promise<SharedResultLink> => {
    const existingLinksQuery = query(
      collection(db, "shared_result_links"),
      where("companyId", "==", request.companyId),
      where("productId", "==", request.productId),
      where("year", "==", request.year)
    );
    
    const existingLinksSnapshot = await getDocs(existingLinksQuery);

    if (!existingLinksSnapshot.empty) {
      const existingLinks = existingLinksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SharedResultLink[];
      
      // Sort by creation date to find the oldest
      existingLinks.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      
      // Delete the oldest link
      const oldestLink = existingLinks[0];
      await deleteDoc(doc(db, "shared_result_links", oldestLink.id));
    }
    
    const shareToken = sharingService.generateUniqueToken();
    
    const sharedLink: Omit<SharedResultLink, 'id'> = {
      companyId: request.companyId,
      companyName: request.companyName,
      productId: request.productId,
      productName: request.productName,
      year: request.year,
      footprintType: request.footprintType,
      shareToken,
      createdAt: new Date(),
      isActive: true,
      viewCount: 0,
      createdBy: request.userId
    };

    const docRef = await addDoc(collection(db, "shared_result_links"), sharedLink);
    
    return {
      id: docRef.id,
      ...sharedLink
    };
  },

  getSharedLinkByToken: async (token: string): Promise<SharedResultLink | null> => {
    try {
      const q = query(collection(db, "shared_result_links"), where("shareToken", "==", token));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as SharedResultLink;
    } catch (error) {
      console.error('Error fetching shared link by token:', error);
      return null;
    }
  },

  // Get all shared links for a company
  getCompanySharedLinks: async (companyId: string): Promise<SharedResultLink[]> => {
    try {
      const q = query(collection(db, "shared_result_links"), where("companyId", "==", companyId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SharedResultLink[];
    } catch (error) {
      console.error('Error fetching company shared links:', error);
      return [];
    }
  },

  incrementViewCount: async (linkId: string): Promise<void> => {
    try {
      const docRef = doc(db, "shared_result_links", linkId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentData = docSnap.data();
        await updateDoc(docRef, {
          viewCount: (currentData.viewCount || 0) + 1,
          lastViewedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  },

  // Deactivate a shared link
  deactivateLink: async (linkId: string): Promise<void> => {
    try {
      const docRef = doc(db, "shared_result_links", linkId);
      await updateDoc(docRef, {
        isActive: false
      });
    } catch (error) {
      console.error('Error deactivating link:', error);
      throw error;
    }
  },

  // Reactivate a shared link
  reactivateLink: async (linkId: string): Promise<void> => {
    try {
      const docRef = doc(db, "shared_result_links", linkId);
      await updateDoc(docRef, {
        isActive: true
      });
    } catch (error) {
      console.error('Error reactivating link:', error);
      throw error;
    }
  },

  // Delete a shared link
  deleteLink: async (linkId: string): Promise<void> => {
    try {
      const docRef = doc(db, "shared_result_links", linkId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting link:', error);
      throw error;
    }
  },

  canUserShare: async (userId: string): Promise<boolean> => {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      
      if (!userDoc.exists()) {
        return false;
      }
      
      const userData = userDoc.data();

      return userData.role === 'company';
    } catch (error) {
      console.error('Error checking if user can share:', error);
      return false;
    }
  }
};

export { sharingService }; 